import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { HERO_VIDEO } from "../data";
import { WhatsAppButton } from "./WhatsAppButton";
import { ShieldCheck, Volume2, VolumeX, RotateCcw, Play } from "lucide-react";

export const Hero = ({ config }) => {
  const videoRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [muted, setMuted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);

  const onTime = () => {
    const v = videoRef.current;
    if (v && v.duration) setProgress((v.currentTime / v.duration) * 100);
  };
  const startPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = false;
    setMuted(false);
    v.play();
    setStarted(true);
  };
  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.paused ? v.play() : v.pause();
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
  };
  const replay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = 0;
    v.play();
    setProgress(0);
  };

  return (
    <section id="top" className="relative flex min-h-[92vh] items-center justify-center overflow-hidden pt-24 pb-14 sm:pt-28">
      <div className="absolute inset-0 grid-overlay opacity-50" />
      <div className="aurora left-[-10%] top-[4%] h-[420px] w-[420px] bg-[#1E90FF]" />
      <div className="aurora right-[-12%] bottom-[2%] h-[460px] w-[460px] bg-[#3B82F6]" />
      <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#070A11] to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-4xl px-4 text-center sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mx-auto mb-4 inline-flex items-center gap-2 rounded-full border border-[#3B82F6]/40 bg-[#3B82F6]/10 px-3.5 py-1.5 backdrop-blur-md"
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#25D366]" />
          </span>
          <span className="text-[11px] font-bold uppercase tracking-wider text-[#60A5FA] sm:text-xs">{config.badge}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading text-[1.9rem] font-extrabold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl"
        >
          {config.line1}
          <br />
          <span className="gradient-text">{config.line2}</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mx-auto mt-4 max-w-2xl text-sm text-gray-300 sm:text-base md:text-lg"
        >
          {config.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="relative mx-auto mt-8 w-full max-w-3xl"
        >
          <div className="absolute -inset-1 rounded-3xl bg-gradient-to-r from-[#1E90FF] to-[#3B82F6] opacity-50 blur-2xl" />
          <div
            className="group relative cursor-pointer select-none overflow-hidden rounded-2xl border border-white/15 bg-black shadow-2xl md:rounded-3xl"
            onClick={togglePlay}
            onContextMenu={(e) => e.preventDefault()}
          >
            <video
              ref={videoRef}
              data-testid="hero-video"
              playsInline
              preload="metadata"
              controlsList="nodownload noplaybackrate"
              disablePictureInPicture
              onContextMenu={(e) => e.preventDefault()}
              onTimeUpdate={onTime}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              className="aspect-video h-full w-full object-cover"
            >
              <source src={HERO_VIDEO} type="video/mp4" />
              <source src="/hero-video.webm" type="video/webm" />
            </video>

            {!playing && (
              <button
                onClick={(e) => { e.stopPropagation(); started ? togglePlay() : startPlay(); }}
                data-testid="video-play-button"
                aria-label="Video abspielen"
                className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-black/35 backdrop-blur-[1px] transition-colors hover:bg-black/25"
              >
                <span className="pulse-ring flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[#1E90FF] to-[#60A5FA] text-[#04070F] shadow-xl md:h-20 md:w-20">
                  <Play className="ml-1 h-7 w-7 md:h-9 md:w-9" fill="currentColor" />
                </span>
                <span className="rounded-full bg-black/50 px-4 py-1.5 text-xs font-bold text-white backdrop-blur-md md:text-sm">
                  {started ? "Weiter abspielen" : "Video ansehen"}
                </span>
              </button>
            )}

            {started && (
              <button
                onClick={toggleMute}
                data-testid="video-sound-toggle"
                className="absolute right-2.5 top-2.5 z-30 flex h-9 w-9 items-center justify-center rounded-full border border-white/20 bg-black/50 text-white backdrop-blur-md transition-transform hover:scale-110 md:h-10 md:w-10"
              >
                {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
              </button>
            )}

            <div className="absolute inset-x-0 bottom-0 z-30 h-1.5 bg-white/10">
              <div className="h-full bg-gradient-to-r from-[#1E90FF] to-[#60A5FA]" style={{ width: `${progress}%` }} />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.45 }}
          className="mt-8 flex flex-col items-center gap-3.5"
        >
          <WhatsAppButton size="lg" testid="hero-whatsapp" label={config.cta} pulse />
          <p className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[12px] text-gray-400 sm:text-sm">
            <ShieldCheck className="h-4 w-4 text-[#60A5FA]" /> {config.microtrust}
          </p>
          {started && (
            <button onClick={replay} data-testid="video-replay" className="inline-flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-white">
              <RotateCcw className="h-3.5 w-3.5" /> Video neu starten
            </button>
          )}
        </motion.div>
      </div>
    </section>
  );
};
